// utils/jobUtils.js

// Helper function to ensure date is in YYYY-MM-DD format without timezone issues
export const ensureDateFormat = (dateValue) => {
  if (!dateValue) return "";

  // If it's already a string in YYYY-MM-DD format, return as is
  if (typeof dateValue === "string" && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateValue;
  }

  // If it's a timestamp string, extract date part
  if (typeof dateValue === "string" && dateValue.includes("T")) {
    return dateValue.split("T")[0];
  }

  // If it's a Date object or needs formatting, use formatExcelDate
  return formatExcelDate(dateValue);
};

// Function to format Excel date (you may need to import this from excelUtils)
export const formatExcelDate = (dateValue) => {
  if (!dateValue) return "";

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0];
  } catch (error) {
    console.warn("Error formatting date:", error);
    return "";
  }
};

// Function to validate file uploads
export const validateFiles = (files) => {
  if (!files.tax || !files.ar04) {
    return {
      valid: false,
      message: "กรุณาเลือกไฟล์ Tax.xlsx และ AR04.xlsx",
    };
  }

  const maxSize = 50 * 1024 * 1024; // 50MB
  if (files.tax.size > maxSize || files.ar04.size > maxSize) {
    return {
      valid: false,
      message: "ไฟล์มีขนาดใหญ่เกินไป (สูงสุด 50MB)",
    };
  }

  return { valid: true };
};

// Function to format file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Function to get file extension
export const getFileExtension = (filename) => {
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
};

// Function to check if file is Excel
export const isExcelFile = (file) => {
  const validExtensions = ["xls", "xlsx"];
  const extension = getFileExtension(file.name).toLowerCase();
  return validExtensions.includes(extension);
};

// Function to create error summary
export const createErrorSummary = (errors) => {
  const errorTypes = {};

  errors.forEach((error) => {
    const remark = error.Remark || "Unknown error";
    if (errorTypes[remark]) {
      errorTypes[remark]++;
    } else {
      errorTypes[remark] = 1;
    }
  });

  return Object.entries(errorTypes)
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => ({ type, count }));
};

// Function to export data to Excel (requires XLSX library)
export const exportToExcel = (data, filename, sheetName = "Sheet1") => {
  if (typeof window.XLSX === "undefined") {
    throw new Error("XLSX library not available");
  }

  const ws = window.XLSX.utils.json_to_sheet(data);
  const wb = window.XLSX.utils.book_new();
  window.XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Auto-size columns
  const range = window.XLSX.utils.decode_range(ws["!ref"]);
  const cols = [];
  for (let C = range.s.c; C <= range.e.c; ++C) {
    let maxWidth = 10;
    for (let R = range.s.r; R <= range.e.r; ++R) {
      const cell = ws[window.XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && cell.v) {
        const cellLength = cell.v.toString().length;
        maxWidth = Math.max(maxWidth, cellLength);
      }
    }
    cols.push({ wch: Math.min(maxWidth, 50) });
  }
  ws["!cols"] = cols;

  window.XLSX.writeFile(wb, filename);
};

// Function to format Thai date
export const formatThaiDate = (date, options = {}) => {
  const defaultOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  };

  return new Date(date).toLocaleDateString("th-TH", defaultOptions);
};

// Function to calculate progress percentage
export const calculateProgress = (current, total) => {
  if (total === 0) return 0;
  return Math.round((current / total) * 100);
};

// Function to get status color
export const getStatusColor = (status) => {
  const statusColors = {
    PENDING: "#f59e0b",
    IN_PROGRESS: "#3b82f6",
    COMPLETED: "#ef4444",
    FIXED: "#10b981",
    CANCELLED: "#6b7280",
  };

  return statusColors[status] || "#6b7280";
};

// Function to get status text
export const getStatusText = (status) => {
  const statusTexts = {
    PENDING: "รอดำเนินการ",
    IN_PROGRESS: "กำลังดำเนินการ",
    COMPLETED: "เสร็จสิ้น (มีข้อผิดพลาด)",
    FIXED: "แก้ไขเรียบร้อย",
    CANCELLED: "ยกเลิก",
  };

  return statusTexts[status] || "ไม่ทราบสถานะ";
};

// Function to generate unique filename
export const generateUniqueFilename = (baseName, extension) => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `${baseName}_${timestamp}.${extension}`;
};

// Function to debounce search input
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
